import {
    extractStyles,
    addRows,
    defaultTemplateModel,
    createRows,
    getRowExtra,
} from './useTemplateModel';
import { load as loadConfig } from './useConfig';

import { stringToHTMLDOM } from './useDOM';

export const renderPDF = (templateEditModel, options = {}) => {
    const newBody = addRows(
        defaultTemplateModel(templateEditModel.template.head),
        createRows(templateEditModel.template.body),
    );
    const head = templateEditModel.template.head;
    let body = '';
    const meta = templateEditModel.template.meta ? templateEditModel.template.meta : {};
    const langCode = (options.langCode || 'cs').toLowerCase();
    // Rows
    for (let r in newBody.template.body) {
        const row = newBody.template.body[r];

        let colsInRow = '';
        // cols
        for (const c in row.cols) {
            const col = row.cols[c];
            colsInRow += createColsPDF(col);
        }

        const rowStyleStr = extractStyles(row.style).join('');
        const rowRole = row.tag === 'div' ? ' role="region"' : '';
        let rowStr = `
    <${row.tag} class="clearfix ${row.cssClass}" style="${rowStyleStr}"${rowRole}>
        ${colsInRow.trim()}
    </${row.tag}>
`;
        rowStr = postprocessPDF(rowStr, row);
        body += `${rowStr.trim()}`;
    }
    const result = createPDFHTML(body, head, meta, langCode);
    return finalSanitizePDFHTML(result);
};

// Actions before save PDF source
export const postprocessPDF = (rowStr, row) => {
    // Here we extend the detection and postprocessing
    let result = applyImageOriginSrc(rowStr);
    result = fixTableIteratorMarkup(result, row);
    result = applyPDFAccessibility(result);
    return result;
};

export const createPDFHTML = (body, head, meta, langCode = 'cs') => {
    let stickyHeader = '';
    let stickyFooter = '';
    if (typeof meta.header === 'string' && meta.header.trim().length > 0) {
        let headerHtml = applyPDFAccessibility(meta.header.trim());
        // Ensure header root element has role="banner" for accessibility
        headerHtml = headerHtml.replace(/^(<\w+)(?![^>]*\brole=)/i, '$1 role="banner"');
        stickyHeader = headerHtml;
    }

    if (typeof meta.footer === 'string' && meta.footer.trim().length > 0) {
        let footerHtml = applyPDFAccessibility(meta.footer.trim());
        // Ensure footer root element has role="contentinfo" for PDF/UA
        footerHtml = footerHtml.replace(/^(<\w+)(?![^>]*\brole=)/i, '$1 role="contentinfo"');
        stickyFooter = footerHtml;
    }

    return `<!DOCTYPE html>
<html lang="${langCode}">
<head>
<meta charset="UTF-8">
<title>${meta.data.title}</title>
<meta name="description" content="${meta.data.description}">
<meta name="keywords" content="${meta.data.keywords}">
<meta name="author" content="${meta.data.author}">
<!-- [#assign templateOriginCode = "${meta.data.originCode}"] -->
${head.styles}
</head>
<body>
${stickyHeader}
${stickyFooter}
<div class="template-base" role="main">
    ${body}
</div>
</body>
</html>`;
};

export const renderEMAIL = (templateEditModel) => {
    const newBody = addRows(
        defaultTemplateModel(templateEditModel.template.head),
        createRows(templateEditModel.template.body),
    );
    const head = templateEditModel.template.head;
    let body = '';
    const meta = templateEditModel.template.meta ? templateEditModel.template.meta : {};
    // Rows
    for (let r in newBody.template.body) {
        const row = newBody.template.body[r];

        let colsInRow = '';
        // cols
        for (const c in row.cols) {
            const col = row.cols[c];
            colsInRow += createCols(col);
        }

        const rowStyleStr = extractStyles(row.style).join('');
        let rowStr = `
    <${row.tag} class="clearfix ${row.cssClass}" style="${rowStyleStr}">
        ${colsInRow.trim()}
    </${row.tag}>
`;
        rowStr = postprocessEmail(rowStr, row);
        body += `${rowStr.trim()}`;
    }
    const result = createEMAILHTML(body, head, meta);
    return result;
};

// Actions before save Email source
export const postprocessEmail = (rowStr, row) => {
    // Here we extend the detection
    let result = applyImageOriginSrc(rowStr);
    result = fixTableIteratorMarkup(result, row);
    return result;
};

export const createEMAILHTML = (body, head, meta) => {
    let stickyFooter = '';
    return `<!DOCTYPE html>
<html lang="cs">
<!-- [#assign templateOriginCode = "${meta.data.originCode}"] -->
<head>
<meta charset="UTF-8">
<title>${meta.data.title}</title>
<meta name="description" content="${meta.data.description}">
<meta name="keywords" content="${meta.data.keywords}">
<meta name="author" content="${meta.data.author}">
${head.styles}
</head>
<body>
<div class="template-base">
    ${body}
</div>
${stickyFooter}
</body>
</html>`;
};

export const renderSMS = (templateEditModel) => {
    return templateEditModel;
};

export const renderINBOX = (templateEditModel) => {
    return templateEditModel;
};

// PDF-specific col rendering with accessibility roles
const createColsPDF = (col) => {
    const colContent = col.content;
    const _cStyle = extractStyles(col.style).join(''),
        _ccStyle = extractStyles(colContent.style).join(''),
        _cTag = col.tag,
        _ccTag = colContent.tag,
        _cCSSClass = col.cssClass,
        _ccCSSClass = colContent.cssClass;

    const colRole = _cTag === 'div' ? ' role="group"' : '';
    const contentRole = _ccTag === 'div' ? ' role="article"' : '';

    return `
        <${_cTag} class="${_cCSSClass}" style="${_cStyle}"${colRole}>
            <${_ccTag} class="${_ccCSSClass}" style="${_ccStyle}"${contentRole}>
                ${colContent.html}
            </${_ccTag}>
        </${_cTag}>
`;
};

// Generate human-readable label from href for PDF/UA link alt text
export const hrefToAltText = (href) => {
    if (!href) return '';
    if (/^https?:\/\//i.test(href)) return `Web: ${href.replace(/^https?:\/\//i, '')}`;
    if (/^mailto:/i.test(href)) return `Email: ${href.replace(/^mailto:/i, '')}`;
    if (/^tel:/i.test(href)) return `Tel: ${href.replace(/^tel:/i, '')}`;
    return href;
};

export const applyImageOriginSrc = (html) => {
    const doc = stringToHTMLDOM(html);

    Array.from(doc.querySelectorAll('img[data-origin-src]')).forEach((image) => {
        const originSrc = image.getAttribute('data-origin-src');

        image.setAttribute('src', originSrc);
        if (isImagePlaceholderSrc(originSrc)) {
            removeImageStyleHeight(image);
        }
        image.removeAttribute('data-origin-src');
        image.removeAttribute('data-editor-width');
        image.removeAttribute('data-editor-height');
    });

    return doc.body.innerHTML;
};

export const isImagePlaceholderSrc = (src) => {
    return /^\[=(item\.[a-zA-Z_]\w*|([a-zA-Z_][\w]*)(?:\[\d+\])\.([a-zA-Z_]\w*))\s*\]$/.test(
        src || '',
    );
};

export const removeImageStyleHeight = (image) => {
    const style = (image.getAttribute('style') || '')
        .split(';')
        .map((item) => item.trim())
        .filter((item) => item.length > 0 && !/^height\s*:/i.test(item))
        .join('; ');

    if (style.length > 0) {
        image.setAttribute('style', `${style};`);
    } else {
        image.removeAttribute('style');
    }
};

// PDF/UA accessibility postprocessing
export const applyPDFAccessibility = (html) => {
    let result = html;

    // Decorative images: ensure alt text, role="presentation" and aria-hidden="true"
    // so the PDF engine tags them as Artifact instead of Figure (PDF/UA 7.3-1)
    result = result.replace(/<img(?![^>]*\balt=)([^>]*?)>/gi, '<img$1 alt="context-image">');
    result = result.replace(/<img([^>]*)\balt\s*=\s*""([^>]*)>/gi, '<img$1alt="context-image"$2>');
    result = result.replace(/<img(?![^>]*\brole=)([^>]*?)>/gi, '<img role="presentation"$1>');
    result = result.replace(/<img(?![^>]*\baria-hidden=)([^>]*?)>/gi, '<img aria-hidden="true"$1>');

    // Add scope="col" to <th> tags without scope attribute
    result = result.replace(/<th\b(?![^>]*\bscope=)([^>]*?)>/gi, '<th scope="col"$1>');

    // Links: add data-pdf-alt from href if not already present (PDF/UA 7.18.1-2, 7.18.5-1, 7.18.5-2)
    result = result.replace(
        /<a\b(?![^>]*\bdata-pdf-alt=)([^>]*\bhref="([^"]*?)"[^>]*)>/gi,
        (match, attrs, href) => `<a data-pdf-alt="${hrefToAltText(href)}"${attrs}>`,
    );

    // Links: add title attribute from href for PDF/UA Contents key
    result = result.replace(
        /<a\b(?![^>]*\btitle=)([^>]*\bhref="([^"]*?)"[^>]*)>/gi,
        '<a title="$2"$1>',
    );

    // Add aria-label to links wrapping only an image (no text content)
    result = result.replace(
        /<a\b([^>]*)>\s*(<img[^>]*>)\s*<\/a>/gi,
        '<a$1 aria-label="link">$2</a>',
    );

    return result;
};

// utils
export const createCols = (col) => {
    const colContent = col.content;
    const _cStyle = extractStyles(col.style).join(''),
        _ccStyle = extractStyles(colContent.style).join(''),
        _cTag = col.tag,
        _ccTag = colContent.tag,
        _cCSSClass = col.cssClass,
        _ccCSSClass = colContent.cssClass;

    return `
        <${_cTag} class="${_cCSSClass}" style="${_cStyle}">
            <${_ccTag} class="${_ccCSSClass}" style="${_ccStyle}">
                ${colContent.html}
            </${_ccTag}>
        </${_cTag}>
`;
};

export const fixTableIteratorMarkup = (rowStr, row) => {
    const iteratorTbodyStartPattern = /<tbody><tr class="iterator-header">/gs;
    const iteratorStartPattern = /<tr class="iterator-body">/gs;
    const iteratorEndPattern = /<\/tbody>/gs;
    const iteratorComponentPattern = /<\/?iterator-(start|end)-component>/gs;

    let result = rowStr;

    if (getRowExtra(row, 'type') === 'iterator') {
        result = result
            .replace(iteratorTbodyStartPattern, '<thead><tr class="iterator-header">')
            .replace(
                iteratorStartPattern,
                `</thead><tbody>[#list ${getRowExtra(row, 'placeholder')} as item]<tr class="iterator-body">`,
            )
            .replace(iteratorEndPattern, '[/#list]</tbody>');
    }
    result = result.replace(iteratorComponentPattern, '');
    // console.log('Postprocessed row:', result);
    return result;
};

export const fixBrokenMarkup = (html) => {
    const rules = [
        {
            search: 'style="width: nullpx;"',
            replace: '',
        },
        {
            search: 'style="width: nullpx"',
            replace: '',
        },
        {
            search: 'style="null"',
            replace: '',
        },
        {
            search: 'class="null"',
            replace: '',
        },
        {
            search: 'class="tpl-p null"',
            replace: 'class="tpl-p"',
        },
        {
            search: ' style="">',
            replace: '>',
        },
        {
            search: ' style>',
            replace: '>',
        },
        {
            search: 'style="break-after: page;"',
            replace: 'style="page-break-after: always; break-after: page;"',
        },
    ];

    /* LAST WORKING
    rules.map((rule) => {
        html = html.replaceAll(rule.search, rule.replace);
    }); */
    rules.forEach((rule) => {
        html = html.replaceAll(rule.search, rule.replace);
    });
    html = fixHTMLTags(html);
    return html;
};

const fixHTMLTags = (html) => {
    // Odstranění nepotřebných mezer před uzavřením tagu
    html = html.replaceAll(/\s+(>)/g, '$1');
    // Odstranění nepotřebných mezer uvnitř tagů
    html = html.replaceAll(/"\s+"/g, '" "');
    return html;
};

export const fixDOM = (html) => {
    let dom = stringToHTMLDOM(html);

    const query = dom.querySelectorAll('th, td');
    for (const el of query) {
        const attributeNodeArray = [...el.attributes];
        const attrs = attributeNodeArray.reduce((attrs, attribute) => {
            attrs[attribute.name] = attribute.value;
            return attrs;
        }, {});
        const style = colWidth(attrs);
        if (style.length > 0) {
            el.style = style;
        }
    }

    const query2 = dom.querySelectorAll('table');
    for (const t of query2) {
        const attributeNodeArray = [...t.attributes];
        const attrs = attributeNodeArray.reduce((attrs, attribute) => {
            attrs[attribute.name] = attribute.value;
            return attrs;
        }, {});
        if (attrs.style) {
            t.style = tableStyleClear(attrs.style);
        } else {
            t.style.width = '740px';
        }
        t.setAttribute('cellspacing', '0');
    }

    const query3 = dom.querySelectorAll('p:empty');
    for (const t of query3) {
        t.innerHTML = '&nbsp;';
    }

    const result = dom.getElementsByTagName('body')[0].innerHTML;
    return result;
};

const tableStyleClear = (style) => {
    const stylesArr = style.split(';');
    const newStyle = [];
    for (const r of stylesArr) {
        const validProp = normalizeStyleProperties(r);
        if (validProp.length > 3) {
            newStyle.push(validProp);
        }
    }
    return newStyle.join(';');
};

const normalizeStyleProperties = (styleItemStr) => {
    const styleItem = styleItemStr.split(':');

    if (styleItem.length !== 2) {
        return '';
    }
    const propName = styleItem[0].trim() || '';
    const propValue = styleItem[1].trim() || '';

    if (propName.length === 0 || propValue.length === 0) {
        return '';
    }

    if (propName === 'width') {
        if (propValue.endsWith('px')) {
            const width = parseInt(propValue.replace('px', ''));
            const realWidth = width > 740 ? 740 : width;
            return `${propName}: ${realWidth}px`;
        }
    }
    if (propName === 'min-width') {
        if (propValue.endsWith('px')) {
            const width = parseInt(propValue.replace('px', ''));
            const realWidth = width > 740 ? 740 : width;
            return `${propName}: ${realWidth}px`;
        }
    }
    return `${propName}: ${propValue}`;
};

const colWidth = (attrs) => {
    if (Object.keys(attrs).includes('colwidth')) {
        const cl = extractColwidth(attrs.colwidth);
        const pixels = cl.reduce((accumulator, currentValue) => {
            return accumulator + parseInt(currentValue);
        }, 0);
        return `width: ${pixels}px`;
    }
    return '';
};

const extractColwidth = (colwidth) => {
    return colwidth.split(',');
};

export const finalSanitizePDFHTML = (html) => {
    // Here we can add additional sanitization rules specific to PDF if needed
    html = enforcePdfLandmarkRoles(html);
    html = enforceFontFamily(html);
    return html;
};

// Ensure PDF landmark roles are present for running header/footer containers
const enforcePdfLandmarkRoles = (html) => {
    let result = html;
    result = result.replace(
        /<(\w+)(?![^>]*\brole=)([^>]*\bid=(["'])header\3[^>]*)>/gi,
        '<$1 role="banner"$2>',
    );
    result = result.replace(
        /<(\w+)(?![^>]*\brole=)([^>]*\bid=(["'])footer\3[^>]*)>/gi,
        '<$1 role="contentinfo"$2>',
    );
    return result;
};

// Get allowed font families from config
const getAllowedFonts = () => {
    const cfg = loadConfig();
    return (cfg.fonts || []).map((f) => f.family);
};

const getDefaultFont = () => {
    const allowed = getAllowedFonts();
    return allowed.length > 0 ? allowed[0] : 'Arial Unicode MS';
};

// Normalize font-family value for comparison (strip quotes, trim)
const normalizeFontName = (name) => name.replace(/["']/g, '').trim();

// Check if a font-family value contains at least one allowed font
const isAllowedFontFamily = (fontFamilyValue, allowedFonts) => {
    const families = fontFamilyValue.split(',').map(normalizeFontName);
    return families.some((f) =>
        allowedFonts.some((allowed) => f.toLowerCase() === allowed.toLowerCase()),
    );
};

// Replace font-family in a CSS declaration string
const replaceFontInStyle = (styleStr, allowedFonts, defaultFont) => {
    return styleStr.replace(/font-family\s*:\s*([^;"]+)/gi, (match, value) => {
        if (isAllowedFontFamily(value, allowedFonts)) return match;
        return `font-family: ${defaultFont}`;
    });
};

// Enforce allowed font-family on final HTML:
// 1) inline style attributes on elements
// 2) <style> tag content
export const enforceFontFamily = (html) => {
    const allowedFonts = getAllowedFonts();
    const defaultFont = getDefaultFont();

    // Replace font-family in inline style attributes
    let result = html.replace(
        /(<[^>]+\bstyle=")([^"]*)(")/gi,
        (match, before, styleValue, after) => {
            if (!/font-family/i.test(styleValue)) return match;
            return before + replaceFontInStyle(styleValue, allowedFonts, defaultFont) + after;
        },
    );

    // Replace font-family inside <style> blocks
    result = result.replace(
        /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi,
        (match, openTag, cssContent, closeTag) => {
            if (!/font-family/i.test(cssContent)) return match;
            return openTag + replaceFontInStyle(cssContent, allowedFonts, defaultFont) + closeTag;
        },
    );

    return result;
};

export const cleanHtml = (htmlString) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach((element) => {
        // Odstranění prázdných atributů 'style' a 'class'
        if (element.getAttribute('style') === '') {
            element.removeAttribute('style');
        }
        if (element.getAttribute('class') === '') {
            element.removeAttribute('class');
        }
    });
    let cleanHtml = tempDiv.innerHTML;
    cleanHtml = cleanHtml.replace(/<(\w+)\s+/g, '<$1 ').replace(/\s+>/g, '>');
    return cleanHtml;
};

export const useTemplateExtras = () => {
    return {
        renderPDF: renderPDF,
        renderEMAIL: renderEMAIL,
        renderSMS: renderSMS,
        renderINBOX: renderINBOX,
        cleanHtml: cleanHtml,
        fixBrokenMarkup: fixBrokenMarkup,
        fixDOM: fixDOM,
        applyPDFAccessibility: applyPDFAccessibility,
        hrefToAltText: hrefToAltText,
        enforceFontFamily: enforceFontFamily,
        finalSanitizePDFHTML: finalSanitizePDFHTML,
    };
};

export default useTemplateExtras;
